const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL ?? "/api/v1";

const APP_BASE_URL = process.env.NEXT_PUBLIC_APP_URL ?? "";

const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Without a bound, a backend that never responds (e.g. choking on
 * pathological input) leaves the caller's promise pending forever, which
 * permanently disables every button gated on that in-flight state. Every
 * request gets a hard ceiling so a hang always surfaces as a catchable
 * error instead of freezing the UI.
 */
function requestTimeoutSignal(): AbortSignal {
  return AbortSignal.timeout(REQUEST_TIMEOUT_MS);
}

/** fetch() rejects a timed-out/aborted request with a DOMException, not an ApiError; normalize it so callers' error handling (which checks `instanceof Error`) shows a clear message instead of "signal timed out". */
function rethrowAsApiError(caught: unknown): never {
  if (caught instanceof DOMException && (caught.name === "TimeoutError" || caught.name === "AbortError")) {
    throw new ApiError("The server is taking too long to respond. Please try again.", 0);
  }
  throw caught;
}

export function buildApiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

export type ApiValidationErrors = Record<string, string[]>;

export class ApiError extends Error {
  status: number;
  errors?: ApiValidationErrors;

  constructor(
    message: string,
    status: number,
    errors?: ApiValidationErrors,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type");
  const isJson = contentType?.includes("application/json");

  const payload = isJson
    ? await response.json()
    : { message: undefined };

  if (!response.ok) {
    const fallbackMessage = response.status === 413
      ? "File size exceeds the 5 MB limit."
      : "The request could not be completed.";

    throw new ApiError(
      payload.message ?? fallbackMessage,
      response.status,
      payload.errors,
    );
  }

  return payload as T;
}

export async function initializeCsrf(): Promise<void> {
  const response = await fetch(`${APP_BASE_URL}/sanctum/csrf-cookie`, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
    cache: "no-store",
    signal: requestTimeoutSignal(),
  }).catch(rethrowAsApiError);

  if (!response.ok && response.status !== 204) {
    throw new ApiError(
      "Unable to initialize the secure session.",
      response.status,
    );
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const isFormData = options.body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: "include",
    cache: "no-store",
    signal: options.signal ?? requestTimeoutSignal(),
    headers: {
      Accept: "application/json",
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    },
  }).catch(rethrowAsApiError);

  return parseResponse<T>(response);
}
